import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useState, useEffect, useCallback, useRef } from 'react';
import { schoolService, School } from '../services/schoolService';
import { courseService, Course } from '../services/courseService';
import { mediaService } from '../services/mediaService';
import CardSkeleton from '../components/CardSkeleton';
import SEO from '../components/SEO';
import Pagination from '../components/Pagination';
import { useBodyScrollLock } from '../hooks/useBodyScrollLock';
import { Search, MapPin, Star, Clock, BookOpen, CheckCircle, X, Filter, Banknote } from 'lucide-react';

function openCourseInNewTab(schoolSlug: string, courseSlug: string) {
  if (!schoolSlug || !courseSlug) return;
  const url = `${window.location.origin}/schools/${encodeURIComponent(schoolSlug)}/${encodeURIComponent(courseSlug)}`;
  window.open(url, '_blank', 'noopener,noreferrer');
}

/** Must match Pagination itemsPerPage for schools tab */
const SCHOOLS_PAGE_SIZE = 12;
/** Yoga style / YA filters are applied in the browser; fetch a large page then slice locally */
const SCHOOLS_CLIENT_FILTER_FETCH_LIMIT = 2000;

/** Upper bound for course-fee range sliders (same numeric scale as stored prices) */
const BUDGET_RANGE_MAX = 50000;
const BUDGET_RANGE_STEP = 500;

function listingBudgetToApi(min: number, max: number): { priceMin?: number; priceMax?: number } {
  const o: { priceMin?: number; priceMax?: number } = {};
  if (min > 0) o.priceMin = min;
  if (max < BUDGET_RANGE_MAX) o.priceMax = max;
  return o;
}

function isListingBudgetActive(min: number, max: number) {
  return min > 0 || max < BUDGET_RANGE_MAX;
}

function formatUsdWhole(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

type ListingLocationSelectProps = {
  value: string;
  onChange: (next: string) => void;
  countries: string[];
  cities: string[];
};

function ListingLocationSelect({ value, onChange, countries, cities }: ListingLocationSelectProps) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full cursor-pointer rounded-xl border-2 border-midnight/10 bg-white px-4 py-3 text-2 text-midnight transition-all focus:border-orange focus:outline-none focus:ring-2 focus:ring-orange"
    >
      <option value="">All locations</option>
      {countries.length > 0 && (
        <optgroup label="Countries">
          {countries.map((c) => (
            <option key={`c-${c}`} value={c}>
              {c}
            </option>
          ))}
        </optgroup>
      )}
      {cities.length > 0 && (
        <optgroup label="Cities">
          {cities.map((c) => (
            <option key={`city-${c}`} value={c}>
              {c}
            </option>
          ))}
        </optgroup>
      )}
    </select>
  );
}

type ListingBudgetSlidersProps = {
  min: number;
  max: number;
  onMinChange: (n: number) => void;
  onMaxChange: (n: number) => void;
};

/** One track, two draggable thumbs (pointer-driven — stacked native ranges steal hits from each other). */
function ListingBudgetSliders({ min, max, onMinChange, onMaxChange }: ListingBudgetSlidersProps) {
  const railRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<'min' | 'max' | null>(null);

  const minPct = (min / BUDGET_RANGE_MAX) * 100;
  const maxPct = (max / BUDGET_RANGE_MAX) * 100;

  const valueFromClientX = (clientX: number): number => {
    const el = railRef.current;
    if (!el) return 0;
    const rect = el.getBoundingClientRect();
    if (rect.width <= 0) return 0;
    const t = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const raw = t * BUDGET_RANGE_MAX;
    const stepped = Math.round(raw / BUDGET_RANGE_STEP) * BUDGET_RANGE_STEP;
    return Math.max(0, Math.min(BUDGET_RANGE_MAX, stepped));
  };

  const pickThumbAt = (clientX: number): 'min' | 'max' => {
    const el = railRef.current;
    if (!el) return 'min';
    const rect = el.getBoundingClientRect();
    const w = rect.width;
    if (w <= 0) return 'min';
    const x = clientX - rect.left;
    const minX = (min / BUDGET_RANGE_MAX) * w;
    const maxX = (max / BUDGET_RANGE_MAX) * w;
    return Math.abs(x - minX) <= Math.abs(x - maxX) ? 'min' : 'max';
  };

  const applyThumb = (thumb: 'min' | 'max', raw: number) => {
    const v = Math.max(0, Math.min(BUDGET_RANGE_MAX, Math.round(raw / BUDGET_RANGE_STEP) * BUDGET_RANGE_STEP));
    if (thumb === 'min') onMinChange(Math.min(v, max));
    else onMaxChange(Math.max(v, min));
  };

  const onRailPointerDown = (e: React.PointerEvent) => {
    if (e.button !== 0) return;
    const thumb = pickThumbAt(e.clientX);
    dragRef.current = thumb;
    applyThumb(thumb, valueFromClientX(e.clientX));
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };

  const onRailPointerMove = (e: React.PointerEvent) => {
    if (dragRef.current == null) return;
    applyThumb(dragRef.current, valueFromClientX(e.clientX));
  };

  const endDrag = (e: React.PointerEvent) => {
    dragRef.current = null;
    try {
      (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    } catch {
      /* not captured */
    }
  };

  const onThumbPointerDown = (thumb: 'min' | 'max', e: React.PointerEvent) => {
    e.stopPropagation();
    if (e.button !== 0) return;
    dragRef.current = thumb;
    applyThumb(thumb, valueFromClientX(e.clientX));
    railRef.current?.setPointerCapture(e.pointerId);
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-between gap-4">
        <div>
          <p className="text-1 font-medium text-midnight/60">Min</p>
          <p className="text-2 font-semibold tabular-nums text-orange">{formatUsdWhole(min)}</p>
        </div>
        <div className="text-right">
          <p className="text-1 font-medium text-midnight/60">Max</p>
          <p className="text-2 font-semibold tabular-nums text-orange">{formatUsdWhole(max)}</p>
        </div>
      </div>

      <div
        ref={railRef}
        className="relative h-12 touch-none select-none rounded-xl border border-midnight/10 bg-fog/40 px-3 py-3"
        onPointerDown={onRailPointerDown}
        onPointerMove={onRailPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onLostPointerCapture={() => {
          dragRef.current = null;
        }}
        role="group"
        aria-label="Course fee range in US dollars"
      >
        <div
          className="pointer-events-none absolute left-3 right-3 top-1/2 h-2 -translate-y-1/2 rounded-full bg-midnight/15"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute top-1/2 h-2 -translate-y-1/2 rounded-full bg-orange"
          style={{
            left: `calc(0.75rem + (100% - 1.5rem) * ${minPct / 100})`,
            width: `calc((100% - 1.5rem) * ${Math.max(0, maxPct - minPct) / 100})`,
          }}
          aria-hidden
        />
        <button
          type="button"
          data-thumb="min"
          className="absolute top-1/2 z-20 h-5 w-5 -translate-x-1/2 -translate-y-1/2 cursor-grab rounded-full border-2 border-white bg-orange shadow-md active:cursor-grabbing"
          style={{ left: `calc(0.75rem + (100% - 1.5rem) * ${minPct / 100})` }}
          aria-label={`Minimum ${formatUsdWhole(min)}`}
          onPointerDown={(e) => onThumbPointerDown('min', e)}
        />
        <button
          type="button"
          data-thumb="max"
          className="absolute top-1/2 z-20 h-5 w-5 -translate-x-1/2 -translate-y-1/2 cursor-grab rounded-full border-2 border-white bg-orange shadow-md active:cursor-grabbing"
          style={{ left: `calc(0.75rem + (100% - 1.5rem) * ${maxPct / 100})` }}
          aria-label={`Maximum ${formatUsdWhole(max)}`}
          onPointerDown={(e) => onThumbPointerDown('max', e)}
        />
      </div>

      <p className="text-1 leading-snug text-midnight/50">
        {
          'Drag either handle or tap the track. Amounts are shown in USD; search uses the same numbers as your listed course fees.'
        }
      </p>
    </div>
  );
}

const Schools = () => {
  const [page, setPage] = useState(1);
  const [searchType, setSearchType] = useState<'schools' | 'courses'>('courses');
  const [schools, setSchools] = useState<School[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<string>('');
  const [selectedStyle, setSelectedStyle] = useState<string>('');
  const [selectedYogaStyles, setSelectedYogaStyles] = useState<string[]>([]);
  const [yogaAllianceCertified, setYogaAllianceCertified] = useState<'yes' | 'no' | 'both'>('both');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [locationCountries, setLocationCountries] = useState<string[]>([]);
  const [locationCities, setLocationCities] = useState<string[]>([]);
  const [budgetMin, setBudgetMin] = useState(0);
  const [budgetMax, setBudgetMax] = useState(BUDGET_RANGE_MAX);
  const [debouncedBudgetMin, setDebouncedBudgetMin] = useState(0);
  const [debouncedBudgetMax, setDebouncedBudgetMax] = useState(BUDGET_RANGE_MAX);
  const [schoolMediaUrls, setSchoolMediaUrls] = useState<Map<string, { images: { logo?: string; coverImage?: string; gallery: string[] }; videos: { introVideo?: string; gallery: string[] } }>>(new Map());
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const resultsHeaderRef = useRef<HTMLDivElement>(null);
  const hasLoadedOnce = useRef(false);

  // Lock body scroll when mobile filter modal is open
  useBodyScrollLock(isMobileFilterOpen);
  const prevFiltersRef = useRef({
    selectedLocation: '',
    selectedStyle: '',
    selectedYogaStyles: [] as string[],
    yogaAllianceCertified: 'both' as 'yes' | 'no' | 'both',
    debouncedSearchTerm: '',
    debouncedBudgetMin: 0,
    debouncedBudgetMax: BUDGET_RANGE_MAX,
    page: 1,
  });

  // Available yoga styles
  const yogaStyles = ['Hatha', 'Ashtanga', 'Vinyasa', 'Yin/Yang', 'Other'];

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { countries, cities } = await schoolService.getListingLocations();
      if (!cancelled) {
        setLocationCountries(countries);
        setLocationCities(cities);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const t = window.setTimeout(() => {
      setDebouncedBudgetMin(budgetMin);
      setDebouncedBudgetMax(budgetMax);
    }, 350);
    return () => window.clearTimeout(t);
  }, [budgetMin, budgetMax]);

  // Fetch schools with filters
  const fetchSchools = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const needsClientOnlyFilters =
        selectedYogaStyles.length > 0 || yogaAllianceCertified !== 'both';

      const params: any = {
        page: needsClientOnlyFilters ? 1 : page,
        limit: needsClientOnlyFilters ? SCHOOLS_CLIENT_FILTER_FETCH_LIMIT : SCHOOLS_PAGE_SIZE,
        sort: 'createdAt',
        order: 'desc',
      };

      if (selectedLocation) {
        params.location = selectedLocation;
      }
      if (selectedStyle) {
        params.style = selectedStyle;
      }
      if (debouncedSearchTerm) {
        params.search = debouncedSearchTerm;
      }

      const response = await schoolService.getSchools(params);
      
      if (response.success && response.data) {
        let filteredSchools = response.data.schools || [];
        
        // Filter by yoga styles (frontend filtering - search in name/description)
        if (selectedYogaStyles.length > 0) {
          filteredSchools = filteredSchools.filter((school: School) => {
            // Search for yoga styles in school name and description
            const schoolText = `${school.name} ${school.description} ${school.shortDescription || ''}`.toLowerCase();
            return selectedYogaStyles.some(style => 
              schoolText.includes(style.toLowerCase())
            );
          });
        }
        
        // Filter by Yoga Alliance Certified
        if (yogaAllianceCertified !== 'both') {
          filteredSchools = filteredSchools.filter((school: School) => {
            if (yogaAllianceCertified === 'yes') {
              return school.yogaAllianceCertified === true;
            } else {
              return school.yogaAllianceCertified !== true;
            }
          });
        }

        let paginatedSchools: School[];
        let totalFiltered: number;
        let pages: number;

        if (needsClientOnlyFilters) {
          totalFiltered = filteredSchools.length;
          pages = Math.max(1, Math.ceil(totalFiltered / SCHOOLS_PAGE_SIZE));
          const startIndex = (page - 1) * SCHOOLS_PAGE_SIZE;
          paginatedSchools = filteredSchools.slice(startIndex, startIndex + SCHOOLS_PAGE_SIZE);
        } else {
          paginatedSchools = filteredSchools;
          totalFiltered = response.data.pagination?.total ?? filteredSchools.length;
          pages = response.data.pagination?.pages ?? Math.max(1, Math.ceil(totalFiltered / SCHOOLS_PAGE_SIZE));
        }
        
        // Convert school media fileKeys to signed URLs
        const mediaUrlMap = new Map<string, { images: { logo?: string; coverImage?: string; gallery: string[] }; videos: { introVideo?: string; gallery: string[] } }>();
        
        // Collect all fileKeys from all schools (filter out URLs)
        const allFileKeys: string[] = [];
        const schoolFileKeysMap = new Map<string, { images: { logo?: string; coverImage?: string; gallery: string[] }; videos: { introVideo?: string; gallery: string[] } }>();
        
        for (const school of paginatedSchools) {
          const schoolKeys: { images: { logo?: string; coverImage?: string; gallery: string[] }; videos: { introVideo?: string; gallery: string[] } } = {
            images: { gallery: [] },
            videos: { gallery: [] }
          };
          
          // Collect fileKeys (not URLs) for this school
          if (school.images?.logo && !school.images.logo.startsWith('http')) {
            schoolKeys.images.logo = school.images.logo;
            allFileKeys.push(school.images.logo);
          }
          if (school.images?.coverImage && !school.images.coverImage.startsWith('http')) {
            schoolKeys.images.coverImage = school.images.coverImage;
            allFileKeys.push(school.images.coverImage);
          }
          if (school.images?.gallery) {
            schoolKeys.images.gallery = school.images.gallery.filter((key: string) => !key.startsWith('http'));
            allFileKeys.push(...schoolKeys.images.gallery);
          }
          if (school.videos?.introVideo && !school.videos.introVideo.startsWith('http')) {
            schoolKeys.videos.introVideo = school.videos.introVideo;
            allFileKeys.push(school.videos.introVideo);
          }
          if (school.videos?.gallery) {
            schoolKeys.videos.gallery = school.videos.gallery.filter((key: string) => !key.startsWith('http'));
            allFileKeys.push(...schoolKeys.videos.gallery);
          }
          
          schoolFileKeysMap.set(school._id, schoolKeys);
        }
        
        // Build display URLs from file keys (domain + key)
        const urlMap = new Map<string, string>();
        allFileKeys.forEach((key) => urlMap.set(key, mediaService.getMediaUrlFromKey(key)));

        for (const school of paginatedSchools) {
          const schoolKeys = schoolFileKeysMap.get(school._id);
          if (schoolKeys) {
            mediaUrlMap.set(school._id, {
              images: {
                logo: schoolKeys.images.logo ? (urlMap.get(schoolKeys.images.logo) || school.images?.logo) : school.images?.logo,
                coverImage: schoolKeys.images.coverImage ? (urlMap.get(schoolKeys.images.coverImage) || school.images?.coverImage) : school.images?.coverImage,
                gallery: schoolKeys.images.gallery.map((key: string) => urlMap.get(key) || key),
              },
              videos: {
                introVideo: schoolKeys.videos.introVideo ? (urlMap.get(schoolKeys.videos.introVideo) || school.videos?.introVideo) : school.videos?.introVideo,
                gallery: schoolKeys.videos.gallery.map((key: string) => urlMap.get(key) || key),
              },
            });
          } else {
            mediaUrlMap.set(school._id, {
              images: {
                logo: school.images?.logo,
                coverImage: school.images?.coverImage,
                gallery: school.images?.gallery || [],
              },
              videos: {
                introVideo: school.videos?.introVideo,
                gallery: school.videos?.gallery || [],
              },
            });
          }
        }
        
        setSchoolMediaUrls(mediaUrlMap);
        setSchools(paginatedSchools);
        setTotalPages(pages);
        setTotalItems(totalFiltered);
        hasLoadedOnce.current = true;
      }
    } catch (err: any) {
      console.error('Error fetching schools:', err);
      setError(err.message || 'Failed to load schools');
      hasLoadedOnce.current = true;
    } finally {
      setLoading(false);
    }
  }, [page, selectedLocation, selectedStyle, selectedYogaStyles, yogaAllianceCertified, debouncedSearchTerm]);

  // Fetch courses with search
  const fetchCourses = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await courseService.searchCourses({
        search: debouncedSearchTerm.trim() || '',
        location: selectedLocation || '',
        yogaStyles: selectedYogaStyles,
        yogaAllianceCertified: yogaAllianceCertified,
        page: page,
        limit: 12,
        sort: 'createdAt',
        order: 'desc',
        ...listingBudgetToApi(debouncedBudgetMin, debouncedBudgetMax),
      });
      
      if (response.success && response.data) {
        const coursesData = response.data.courses || [];
        
        // Convert media fileKeys to signed URLs for all courses
        const coursesWithUrls = await Promise.all(
          coursesData.map(async (course: Course) => {
            if (course.media) {
              const convertedMedia = await mediaService.convertMediaToUrls(course.media);
              return {
                ...course,
                media: {
                  images: convertedMedia.images || [],
                  videos: convertedMedia.videos || [],
                },
              };
            }
            return course;
          })
        );
        
        setCourses(coursesWithUrls);
        setTotalPages(response.data.pagination?.pages || 1);
        setTotalItems(response.data.pagination?.total || 0);
        hasLoadedOnce.current = true;
      }
    } catch (err: any) {
      console.error('Error searching courses:', err);
      setError(err.message || 'Failed to search courses');
      setCourses([]);
      hasLoadedOnce.current = true;
    } finally {
      setLoading(false);
    }
  }, [page, selectedLocation, selectedYogaStyles, yogaAllianceCertified, debouncedSearchTerm, debouncedBudgetMin, debouncedBudgetMax]);

  // Debounce search term - wait 1.5 seconds after user stops typing
  useEffect(() => {
    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Set new timeout to update debouncedSearchTerm after 1.5 seconds
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setPage(1);
    }, 1500);

    // Cleanup function to clear timeout on unmount or when searchTerm changes
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm]);


  // Fetch data based on search type
  useEffect(() => {
    if (searchType === 'schools') {
      fetchSchools();
    } else {
      fetchCourses();
    }
  }, [searchType, fetchSchools, fetchCourses]);

  // Reset to page 1 when search type changes
  useEffect(() => {
    setPage(1);
  }, [searchType]);

  // Scroll to results header when filters change (but not on initial load)
  useEffect(() => {
    // Don't scroll on initial load - wait for data to load at least once
    if (!hasLoadedOnce.current) {
      return;
    }

    const currentFilters = {
      selectedLocation,
      selectedStyle,
      selectedYogaStyles,
      yogaAllianceCertified,
      debouncedSearchTerm,
      debouncedBudgetMin,
      debouncedBudgetMax,
      page,
    };

    const filtersChanged = 
      prevFiltersRef.current.selectedLocation !== currentFilters.selectedLocation ||
      prevFiltersRef.current.selectedStyle !== currentFilters.selectedStyle ||
      JSON.stringify(prevFiltersRef.current.selectedYogaStyles) !== JSON.stringify(currentFilters.selectedYogaStyles) ||
      prevFiltersRef.current.yogaAllianceCertified !== currentFilters.yogaAllianceCertified ||
      prevFiltersRef.current.debouncedSearchTerm !== currentFilters.debouncedSearchTerm ||
      prevFiltersRef.current.debouncedBudgetMin !== currentFilters.debouncedBudgetMin ||
      prevFiltersRef.current.debouncedBudgetMax !== currentFilters.debouncedBudgetMax ||
      prevFiltersRef.current.page !== currentFilters.page;
    
    if (!filtersChanged) {
      return;
    }
    
    // Update previous filters
    prevFiltersRef.current = { ...currentFilters };
    
    const timer = setTimeout(() => {
      if (resultsHeaderRef.current) {
        resultsHeaderRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [selectedLocation, selectedStyle, selectedYogaStyles, yogaAllianceCertified, debouncedSearchTerm, debouncedBudgetMin, debouncedBudgetMax, page]);

  // JavaScript-based sticky sidebar implementation
  useEffect(() => {
    if (!sidebarRef.current || window.innerWidth < 1024) return;

    const element = sidebarRef.current;
    const stickyTop = 96; // px offset from top
    let stickyState: 'static' | 'fixed' | 'absolute' = 'static';

    const updatePosition = () => {
      const container = element.parentElement; // sidebar container
      if (!container) return;

      // Keep container height at least as tall as main content to allow docking
      const mainContent = document.querySelector('.schools-main-content');
      if (mainContent) {
        const mainRect = mainContent.getBoundingClientRect();
        const minHeight = Math.max(mainRect.height, 500); // Ensure minimum 500px height
        container.style.minHeight = `${minHeight}px`;
      }

      const rect = container.getBoundingClientRect();
      const elementHeight = element.offsetHeight;
      const scrollY = window.scrollY || window.pageYOffset;

      const containerTop = rect.top + scrollY;
      const containerBottom = rect.bottom + scrollY;
      const fixedTop = scrollY + stickyTop;
      const fixedBottom = fixedTop + elementHeight;

      let targetState: 'static' | 'fixed' | 'absolute' = 'static';

      if (fixedTop < containerTop) {
        targetState = 'static';
      } else if (fixedBottom > containerBottom) {
        targetState = 'absolute';
      } else {
        targetState = 'fixed';
      }

      if (targetState !== stickyState) {
        stickyState = targetState;

        if (targetState === 'fixed') {
          element.style.position = 'fixed';
          element.style.top = `${stickyTop}px`;
          element.style.left = `${rect.left}px`;
          element.style.width = `${rect.width}px`;
          element.style.bottom = '';
          element.style.zIndex = '10';
        } else if (targetState === 'absolute') {
          element.style.position = 'absolute';
          element.style.top = `${containerBottom - containerTop - elementHeight}px`;
          element.style.left = '0';
          element.style.width = '100%';
          element.style.bottom = '';
          element.style.zIndex = '10';
        } else {
          element.style.position = 'static';
          element.style.top = '';
          element.style.left = '';
          element.style.width = '';
          element.style.bottom = '';
          element.style.zIndex = '';
        }
      } else if (targetState === 'fixed') {
        // keep width/left synced on scroll/resize
        element.style.left = `${rect.left}px`;
        element.style.width = `${rect.width}px`;
      }
    };

    window.addEventListener('scroll', updatePosition, { passive: true });
    window.addEventListener('resize', updatePosition, { passive: true });
    updatePosition();

    return () => {
      window.removeEventListener('scroll', updatePosition);
      window.removeEventListener('resize', updatePosition);
    };
  }, [schools, courses, loading]);

  return (
    <div style={{ position: 'relative', zIndex: 1, minHeight: '100vh' }}>
      <SEO
        title="Find Your Perfect Yoga School - Browse Certified Yoga Teacher Training Programs"
        description="Explore our curated collection of certified yoga schools worldwide. Each school is carefully vetted for quality, authenticity, and student satisfaction. Find the perfect yoga teacher training program for your journey."
        keywords="yoga schools, yoga teacher training programs, certified yoga schools, yoga alliance schools, yoga training rishikesh, yoga school finder, best yoga schools"
        url="https://bookmyyogattc.com"
        canonical="https://bookmyyogattc.com"
      />
      <div className="pt-20 min-h-screen bg-fog" style={{ position: 'relative', zIndex: 1 }}>
      {/* Page Header */}
      <section className="bg-white border-b border-midnight/5 py-12" style={{ position: 'relative', zIndex: 1 }}>
        <div className="container-custom">
          <div className="max-w-4xl">
            <h1 className="text-5 md:text-5 font-serif font-bold text-midnight mb-4 tracking-tight">Find Your Perfect Yoga Teacher Training</h1>
            <p className="text-4 md:text-3 text-midnight/70 leading-relaxed">
              Explore authentic yoga teacher training courses from certified schools worldwide. Start your journey to becoming a certified yoga instructor today.
            </p>
          </div>
        </div>
      </section>

      {/* Filters and Schools Section */}
      <section className="py-8" style={{ position: 'relative', zIndex: 1 }}>
        <div className="container-custom">
          <div className="flex flex-col lg:flex-row lg:items-start gap-8">
            {/* Left Sidebar - Filters - Sticky */}
            <div className="hidden lg:block lg:w-72 lg:flex-shrink-0 relative">
              <div
                ref={sidebarRef}
                className="max-h-[calc(100vh-5.5rem)] overflow-y-auto overscroll-contain rounded-2xl border border-midnight/5 bg-white p-4 shadow-sm [-webkit-overflow-scrolling:touch]"
              >
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                >
                <div className="flex items-center gap-2 mb-4 pb-3 border-b border-midnight/10">
                  <Search className="w-5 h-5 text-orange" />
                  <h3 className="heading-section font-serif font-semibold text-midnight">Filters</h3>
                </div>
                
              {/* Search Type Toggle */}
              <div className="mb-4">
                <label className="block text-2 font-semibold text-midnight mb-3">Search Type</label>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setSearchType('courses');
                      setSearchTerm('');
                      setDebouncedSearchTerm('');
                      setSelectedLocation('');
                      setSelectedStyle('');
                      setSelectedYogaStyles([]);
                      setYogaAllianceCertified('both');
                      setBudgetMin(0);
                      setBudgetMax(BUDGET_RANGE_MAX);
                      setPage(1);
                      setTimeout(() => {
                        if (resultsHeaderRef.current) {
                          resultsHeaderRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }
                      }, 100);
                    }}
                    className={`flex-1 px-4 py-2.5 text-2 rounded-xl font-semibold transition-all ${
                      searchType === 'courses'
                        ? 'bg-orange text-white shadow-md hover:bg-orange-dark'
                        : 'bg-fog text-midnight/70 hover:bg-fog-dark'
                    }`}
                  >
                    Courses
                  </button>
                  {/* Schools search type hidden — courses-only listing for now
                  <button
                    onClick={() => {
                      setSearchType('schools');
                      setSearchTerm('');
                      setDebouncedSearchTerm('');
                      setSelectedLocation('');
                      setSelectedStyle('');
                      setSelectedYogaStyles([]);
                      setYogaAllianceCertified('both');
                      setBudgetMin(0);
                      setBudgetMax(BUDGET_RANGE_MAX);
                      setPage(1);
                      setTimeout(() => {
                        if (resultsHeaderRef.current) {
                          resultsHeaderRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }
                      }, 100);
                    }}
                    className={`flex-1 px-4 py-2.5 text-2 rounded-xl font-semibold transition-all ${
                      searchType === 'schools'
                        ? 'bg-orange text-white shadow-md hover:bg-orange-dark'
                        : 'bg-fog text-midnight/70 hover:bg-fog-dark'
                    }`}
                  >
                    Schools
                  </button>
                  */}
                </div>
              </div>
                
              {/* Search */}
                <div className="mb-4">
                  <label className="block text-2 font-semibold text-midnight mb-2">Search</label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-midnight/40" />
                <input
                  type="text"
                        placeholder="Search courses..."
                  value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                    }}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        if (searchTimeoutRef.current) {
                          clearTimeout(searchTimeoutRef.current);
                        }
                        setDebouncedSearchTerm(searchTerm);
                        setPage(1);
                      }
                    }}
                        className="w-full pl-10 pr-4 py-3 text-2 border-2 border-midnight/10 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange focus:border-orange transition-all text-midnight bg-white placeholder-midnight/40"
                />
                  </div>
              </div>

              {/* Location — countries first, then cities */}
              <div className="mb-4">
                <label className="mb-2 flex items-center gap-2 text-2 font-semibold text-midnight">
                  <MapPin className="h-4 w-4 text-orange" />
                  Location
                </label>
                <ListingLocationSelect
                  value={selectedLocation}
                  onChange={(v) => {
                    setSelectedLocation(v);
                    setPage(1);
                  }}
                  countries={locationCountries}
                  cities={locationCities}
                />
              </div>

              <div className="mb-4">
                <label className="mb-2 flex items-center gap-2 text-2 font-semibold text-midnight">
                  <Banknote className="h-4 w-4 text-orange" />
                  Budget (course fee)
                </label>
                <ListingBudgetSliders
                  min={budgetMin}
                  max={budgetMax}
                  onMinChange={(n) => {
                    setBudgetMin(n);
                    setPage(1);
                  }}
                  onMaxChange={(n) => {
                    setBudgetMax(n);
                    setPage(1);
                  }}
                />
              </div>

              {/* Yoga Styles Filter - Checkboxes */}
              <div className="mb-4">
                <label className="block text-2 font-semibold text-midnight mb-2 flex items-center gap-2">
                  <BookOpen className="w-4 h-4 text-orange" />
                  Yoga Styles
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {yogaStyles.map((style) => (
                    <label
                      key={style}
                      className="flex items-center gap-2 cursor-pointer hover:bg-fog px-2 py-1 rounded transition-colors"
                    >
                      <input
                        type="checkbox"
                        checked={selectedYogaStyles.includes(style)}
                    onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedYogaStyles([...selectedYogaStyles, style]);
                          } else {
                            setSelectedYogaStyles(selectedYogaStyles.filter(s => s !== style));
                          }
                          setPage(1);
                        }}                     className="w-4 h-4 rounded border-midnight/20 cursor-pointer focus:outline-none focus:ring-0 focus:ring-offset-0 transition-colors checked:border-orange checked:bg-orange checked:ring-0 checked:ring-offset-0"
                      />
                      <span className="text-2 text-midnight/70">{style}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Yoga Alliance Certified Filter */}
              <div className="mb-4">
                <label className="block text-2 font-semibold text-midnight mb-2 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-orange" />
                  Yoga Alliance Certified
                </label>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setYogaAllianceCertified('both');
                      setPage(1);
                    }}
                    className={`flex-1 px-4 py-2.5 text-2 rounded-xl font-semibold transition-all ${
                      yogaAllianceCertified === 'both'
                        ? 'bg-orange text-white shadow-md hover:bg-orange-dark'
                        : 'bg-fog text-midnight/70 hover:bg-fog-dark'
                    }`}
                  >
                    Both
                  </button>
                  <button
                    onClick={() => {
                      setYogaAllianceCertified('yes');
                      setPage(1);
                    }}
                    className={`flex-1 px-4 py-2.5 text-2 rounded-xl font-semibold transition-all ${
                      yogaAllianceCertified === 'yes'
                        ? 'bg-orange text-white shadow-md hover:bg-orange-dark'
                        : 'bg-fog text-midnight/70 hover:bg-fog-dark'
                    }`}
                  >
                    Yes
                  </button>
                  <button
                    onClick={() => {
                      setYogaAllianceCertified('no');
                      setPage(1);
                    }}
                    className={`flex-1 px-4 py-2.5 text-2 rounded-xl font-semibold transition-all ${
                      yogaAllianceCertified === 'no'
                        ? 'bg-orange text-white shadow-md hover:bg-orange-dark'
                        : 'bg-fog text-midnight/70 hover:bg-fog-dark'
                    }`}
                  >
                    No
                  </button>
                </div>
              </div>

              </motion.div>
              </div>
            </div>

            {/* Right Section - Results Grid */}
            <div className="flex-1 min-w-0 schools-main-content lg:min-h-[714px]">
              {/* Mobile Filter Buttons */}
              <div className="lg:hidden mb-6">
                <div className="flex gap-3">
                  {(selectedLocation ||
                    selectedStyle ||
                    selectedYogaStyles.length > 0 ||
                    yogaAllianceCertified !== 'both' ||
                    searchTerm ||
                    isListingBudgetActive(budgetMin, budgetMax)) && (
                    <button
                      onClick={() => {
                        setSelectedLocation('');
                        setSelectedStyle('');
                        setSelectedYogaStyles([]);
                        setYogaAllianceCertified('both');
                        setSearchTerm('');
                        setDebouncedSearchTerm('');
                        setBudgetMin(0);
                        setBudgetMax(BUDGET_RANGE_MAX);
                        setPage(1);
                        setTimeout(() => {
                          if (resultsHeaderRef.current) {
                            resultsHeaderRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                          }
                        }, 100);
                      }}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-3 border-2 border-gray-300 text-gray-700 rounded-xl font-semibold text-2 hover:bg-gray-50 transition-colors"
                    >
                      Clear
                    </button>
                  )}
                  <button
                    onClick={() => setIsMobileFilterOpen(true)}
                    className={`flex items-center justify-center gap-2 px-4 py-3 bg-orange text-white rounded-xl font-semibold text-2 hover:bg-orange-dark transition-colors shadow-md ${
                      selectedLocation ||
                      selectedStyle ||
                      selectedYogaStyles.length > 0 ||
                      yogaAllianceCertified !== 'both' ||
                      searchTerm ||
                      isListingBudgetActive(budgetMin, budgetMax)
                        ? 'flex-1' 
                        : 'w-full'
                    }`}
                  >
                    <Filter className="w-5 h-5" />
                    <span>Apply Filters</span>
                    {(selectedLocation ||
                      selectedStyle ||
                      selectedYogaStyles.length > 0 ||
                      yogaAllianceCertified !== 'both' ||
                      searchTerm ||
                      isListingBudgetActive(budgetMin, budgetMax)) && (
                      <span className="ml-1 px-2 py-0.5 bg-white/20 rounded-full text-1">
                        {
                          [
                            selectedLocation,
                            selectedStyle,
                            ...selectedYogaStyles,
                            yogaAllianceCertified !== 'both' ? 'Certified' : '',
                            searchTerm ? 'Search' : '',
                            isListingBudgetActive(budgetMin, budgetMax) ? 'Budget' : '',
                          ].filter(Boolean).length
                        }
                      </span>
                    )}
                  </button>
                </div>
              </div>

              {/* Mobile Filters - Hidden (moved to modal) */}
              <div className="lg:hidden mb-6 hidden">
                <motion.div
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                  className="bg-white rounded-2xl shadow-sm border border-midnight/5 p-4"
                >
                  <div className="flex items-center gap-2 mb-4 pb-3 border-b border-midnight/10">
                    <Search className="w-5 h-5 text-orange" />
                    <h3 className="heading-section font-serif font-semibold text-midnight">Filters</h3>
                  </div>
                  
                  {/* Search Type Toggle */}
                  <div className="mb-4">
                    <label className="block text-2 font-semibold text-midnight mb-2">Search Type</label>
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          setSearchType('courses');
                          setSearchTerm('');
                          setDebouncedSearchTerm('');
                          setSelectedLocation('');
                          setSelectedStyle('');
                          setSelectedYogaStyles([]);
                          setYogaAllianceCertified('both');
                          setBudgetMin(0);
                        setBudgetMax(BUDGET_RANGE_MAX);
                          setPage(1);
                          setTimeout(() => {
                            if (resultsHeaderRef.current) {
                              resultsHeaderRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                            }
                          }, 100);
                        }}
                        className={`flex-1 px-4 py-2.5 text-2 rounded-xl font-semibold transition-all ${
                          searchType === 'courses'
                            ? 'bg-orange text-white shadow-md hover:bg-orange-dark'
                            : 'bg-fog text-midnight/70 hover:bg-fog-dark'
                        }`}
                      >
                        Courses
                      </button>
                      {/* Schools search type hidden — courses-only listing for now
                      <button
                        onClick={() => {
                          setSearchType('schools');
                          setSearchTerm('');
                          setDebouncedSearchTerm('');
                          setSelectedLocation('');
                          setSelectedStyle('');
                          setSelectedYogaStyles([]);
                          setYogaAllianceCertified('both');
                          setBudgetMin(0);
                        setBudgetMax(BUDGET_RANGE_MAX);
                          setPage(1);
                          setTimeout(() => {
                            if (resultsHeaderRef.current) {
                              resultsHeaderRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                            }
                          }, 100);
                        }}
                        className={`flex-1 px-4 py-2.5 text-2 rounded-xl font-semibold transition-all ${
                          searchType === 'schools'
                            ? 'bg-orange text-white shadow-md hover:bg-orange-dark'
                            : 'bg-fog text-midnight/70 hover:bg-fog-dark'
                        }`}
                      >
                        Schools
                      </button>
                      */}
                    </div>
                  </div>
                  
                  {/* Search */}
                  <div className="mb-4">
                    <label className="block text-2 font-semibold text-midnight mb-2">Search</label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-midnight/40" />
                      <input
                        type="text"
                        placeholder="Search courses..."
                        value={searchTerm}
                        onChange={(e) => {
                          setSearchTerm(e.target.value);
                        }}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            if (searchTimeoutRef.current) {
                              clearTimeout(searchTimeoutRef.current);
                            }
                            setDebouncedSearchTerm(searchTerm);
                            setPage(1);
                          }
                        }}
                        className="w-full pl-10 pr-4 py-3 text-2 border-2 border-midnight/10 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange focus:border-orange transition-all text-midnight bg-white placeholder-midnight/40"
                      />
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="mb-2 flex items-center gap-2 text-2 font-semibold text-midnight">
                      <MapPin className="h-4 w-4 text-orange" />
                      Location
                    </label>
                    <ListingLocationSelect
                      value={selectedLocation}
                      onChange={(v) => {
                        setSelectedLocation(v);
                        setPage(1);
                      }}
                      countries={locationCountries}
                      cities={locationCities}
                    />
                  </div>

                  <div className="mb-4">
                    <label className="mb-2 flex items-center gap-2 text-2 font-semibold text-midnight">
                      <Banknote className="h-4 w-4 text-orange" />
                      Budget (course fee)
                    </label>
                    <ListingBudgetSliders
                      min={budgetMin}
                      max={budgetMax}
                      onMinChange={(n) => {
                        setBudgetMin(n);
                        setPage(1);
                      }}
                      onMaxChange={(n) => {
                        setBudgetMax(n);
                        setPage(1);
                      }}
                    />
                  </div>

                  {/* Yoga Styles Filter */}
                  <div className="mb-4">
                    <label className="block text-2 font-semibold text-midnight mb-2 flex items-center gap-2">
                      <BookOpen className="w-4 h-4 text-orange" />
                      Yoga Styles
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      {yogaStyles.map((style) => (
                        <label
                          key={style}
                          className="flex items-center gap-2 cursor-pointer hover:bg-fog px-2 py-1 rounded transition-colors"
                        >
                          <input
                            type="checkbox"
                            checked={selectedYogaStyles.includes(style)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedYogaStyles([...selectedYogaStyles, style]);
                              } else {
                                setSelectedYogaStyles(selectedYogaStyles.filter(s => s !== style));
                              }
                              setPage(1);
                            }}
                            className="w-4 h-4 rounded border-midnight/20 focus:ring-2 focus:ring-orange cursor-pointer"
                          />
                          <span className="text-2 text-midnight/70">{style}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Yoga Alliance Certified Filter */}
                  <div className="mb-4">
                    <label className="block text-2 font-semibold text-midnight mb-2 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-orange" />
                      Yoga Alliance Certified
                    </label>
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          setYogaAllianceCertified('both');
                          setPage(1);
                        }}
                        className={`flex-1 px-4 py-2.5 text-2 rounded-xl font-semibold transition-all ${
                          yogaAllianceCertified === 'both'
                            ? 'bg-orange text-white shadow-md hover:bg-orange-dark'
                            : 'bg-fog text-midnight/70 hover:bg-fog-dark'
                        }`}
                      >
                        Both
                      </button>
                      <button
                        onClick={() => {
                          setYogaAllianceCertified('yes');
                          setPage(1);
                        }}
                        className={`flex-1 px-4 py-2.5 text-2 rounded-xl font-semibold transition-all ${
                          yogaAllianceCertified === 'yes'
                            ? 'bg-orange text-white shadow-md hover:bg-orange-dark'
                            : 'bg-fog text-midnight/70 hover:bg-fog-dark'
                        }`}
                      >
                        Yes
                      </button>
                      <button
                        onClick={() => {
                          setYogaAllianceCertified('no');
                          setPage(1);
                        }}
                        className={`flex-1 px-4 py-2.5 text-2 rounded-xl font-semibold transition-all ${
                          yogaAllianceCertified === 'no'
                            ? 'bg-orange text-white shadow-md hover:bg-orange-dark'
                            : 'bg-fog text-midnight/70 hover:bg-fog-dark'
                        }`}
                      >
                        No
                      </button>
                    </div>
                  </div>

                  {/* Results Count */}
                  <div className="pt-4 border-t border-midnight/10">
                    <div className="bg-fog rounded-xl p-3 border border-midnight/5">
                      <p className="text-2 font-semibold text-midnight mb-1">
                        {loading ? 'Loading...' : `${totalItems} ${searchType === 'schools' ? 'schools' : 'courses'} found`}
                      </p>
                      {(selectedLocation ||
                        selectedStyle ||
                        selectedYogaStyles.length > 0 ||
                        yogaAllianceCertified !== 'both' ||
                        searchTerm ||
                        isListingBudgetActive(budgetMin, budgetMax)) && (
                        <button
                          onClick={() => {
                            setSelectedLocation('');
                            setSelectedStyle('');
                            setSelectedYogaStyles([]);
                            setYogaAllianceCertified('both');
                            setSearchTerm('');
                            setDebouncedSearchTerm('');
                            setBudgetMin(0);
                            setBudgetMax(BUDGET_RANGE_MAX);
                            setPage(1);
                          }}
                          className="text-2 text-orange hover:text-orange-dark font-medium mt-2 transition-colors"
                        >
                          Clear All Filters
                        </button>
                      )}
                    </div>
                  </div>
                </motion.div>
              </div>
              {loading && (
                <>
                  <div ref={resultsHeaderRef} className="mb-6">
                    <h2 className="text-5 font-serif font-semibold text-midnight mb-2">
                      {searchType === 'schools' ? 'Available Schools' : 'Available Courses'}
                    </h2>
                    <p className="text-midnight/60">Loading...</p>
                  </div>
                  <div className="space-y-4">
                    <CardSkeleton count={6} type="rectangular" />
                  </div>
                </>
              )}

              {!loading && error && (
                <div className="text-center py-16 bg-white rounded-xl border border-midnight/5 shadow-sm">
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-5">⚠️</span>
                </div>
                  <h3 className="text-5 font-serif font-semibold text-midnight mb-2">Error loading {searchType}</h3>
                  <p className="text-2 text-midnight/60 mb-6 max-w-md mx-auto">{error}</p>
                  <button
                    onClick={() => searchType === 'schools' ? fetchSchools() : fetchCourses()}
                    className="bg-orange text-white px-6 py-3 rounded-lg text-2 font-semibold hover:bg-orange-dark transition-all duration-300 shadow-sm hover:shadow-md"
                  >
                    Try Again
                  </button>
            </div>
              )}

              {!loading && !error && searchType === 'courses' && courses.length === 0 && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-center py-16 bg-white rounded-xl border border-midnight/5 shadow-sm"
                >
                  <div className="w-16 h-16 bg-fog rounded-full flex items-center justify-center mx-auto mb-4">
                    <Search className="w-8 h-8 text-midnight/40" />
                  </div>
                  <h3 className="text-5 font-serif font-semibold text-midnight mb-2">No courses found</h3>
                  <p className="text-2 text-midnight/60 mb-6 max-w-md mx-auto">Try searching with different keywords like "200 hour", "yoga teacher training", or "meditation"</p>
                  <button
                    onClick={() => {
                      setSearchTerm('');
                      setDebouncedSearchTerm('');
                      setSelectedLocation('');
                      setSelectedStyle('');
                      setSelectedYogaStyles([]);
                      setYogaAllianceCertified('both');
                      setBudgetMin(0);
                      setBudgetMax(BUDGET_RANGE_MAX);
                      setPage(1);
                    }}
                    className="bg-orange text-white px-6 py-3 rounded-lg text-2 font-semibold hover:bg-orange-dark transition-all duration-300 shadow-sm hover:shadow-md"
                  >
                    Clear All Filters
                  </button>
                </motion.div>
              )}

              {!loading && !error && searchType === 'courses' && courses.length > 0 && (
                <>
                  <div ref={resultsHeaderRef} className="mb-6">
                    <h2 className="heading-section font-serif font-semibold text-midnight mb-2">Available Courses</h2>
                    <p className="text-midnight/60">Showing {courses.length} of {totalItems} courses</p>
                  </div>
                  <div className="space-y-4">
                    {courses.map((course, index) => {
                        const schoolName = typeof course.schoolId === 'object' && course.schoolId?.name ? course.schoolId.name : 'Unknown School';
                        const schoolLocation = typeof course.schoolId === 'object' 
                          ? [course.schoolId.city, course.schoolId.country].filter(Boolean).join(', ') || course.schoolId.location || ''
                          : '';
                        const schoolSlug = typeof course.schoolId === 'object' && course.schoolId?.slug ? course.schoolId.slug : '';

                        const formatCurrency = (amount: number, currency: string = 'USD') => {
                          return new Intl.NumberFormat('en-US', {
                            style: 'currency',
                            currency: currency,
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                          }).format(amount);
                        };

                        return (
                          <motion.div
                            key={course._id}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: index * 0.05 }}
                            className="bg-white rounded-xl shadow-sm border border-midnight/5 overflow-hidden hover:shadow-md transition-all duration-300 cursor-pointer group min-h-[280px] md:h-[280px] relative"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              openCourseInNewTab(schoolSlug, course.slug);
                            }}
                          >
                            <div className="flex flex-col md:flex-row h-full">
                              {/* Left Side - Image */}
                              <div className="w-full md:w-64 lg:w-80 h-48 md:h-full flex-shrink-0 bg-fog relative">
                                {/* Yoga Alliance Verified Badge - Top Right of Image */}
                                {course.yogaAllianceCertified && (
                                  <div className="absolute top-3 right-3 z-30">
                                    <div className="relative inline-flex items-center justify-center">
                                      <div className="relative rounded-full" style={{ 
                                        width: '65px', 
                                        height: '65px', 
                                        background: 'white',
                                        border: '3px dashed #16a34a',
                                        padding: '4px',
                                      }}>
                                        <img 
                                          src="/images/yoga_alliance.webp" 
                                          alt="Yoga Alliance Certified" 
                                          className="rounded-full object-contain w-full h-full"
                                          style={{ display: 'block' }}
                                          onError={(e) => {
                                            console.error('Failed to load yoga alliance image');
                                            const target = e.target as HTMLImageElement;
                                            target.style.display = 'none';
                                          }}
                                        />
                                      </div>
                                      <div className="absolute -bottom-1 -right-1 flex items-center justify-center" style={{ width: '28px', height: '28px' }}>
                                        <svg className="w-full h-full" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                          <circle cx="12" cy="12" r="10" stroke="#16a34a" strokeWidth="2" fill="white" />
                                          <path d="M9 12l2 2 4-4" stroke="#16a34a" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                                        </svg>
                                      </div>
                                    </div>
                                  </div>
                                )}
                                {course.media?.images?.[0] ? (
                                  <img
                                    src={course.media.images[0]}
                                    alt={course.name}
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                    loading="lazy"
                                    onError={(e) => {
                                      const target = e.target as HTMLImageElement;
                                      target.style.display = 'none';
                                      e.stopPropagation();
                                    }}
                                  />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center">
                                    <BookOpen className="h-16 w-16 text-midnight/20" />
                                  </div>
                                )}
                                <div className="absolute top-3 right-3 flex flex-col gap-2 z-20">
                                  {course.isFeatured && (
                                    <div className="bg-orange text-white px-3 py-1.5 rounded-lg text-1 font-semibold shadow-lg">
                                      Featured
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Right Side - Content */}
                              <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden p-6">
                                {/* Header Section */}
                                <div className="mb-4 shrink-0">
                                  <h2 className="text-3 sm:text-4 font-serif font-semibold text-midnight mb-2 line-clamp-2 leading-snug group-hover:text-orange transition-colors">
                                    {course.name}
                                  </h2>
                                  <div className="mb-3">
                                    <p className="font-semibold text-midnight/80 text-2 mb-1.5">
                                      {schoolName}
                                    </p>
                                    {schoolLocation && (
                                      <div className="flex items-center gap-1.5 text-midnight/60 text-2">
                                        <MapPin className="h-3.5 w-3.5 text-orange" />
                                        <span>{schoolLocation}</span>
                                      </div>
                                    )}
                                  </div>
                                  <div className="flex flex-wrap gap-2">
                                    <div className="flex items-center gap-1.5 px-2 py-1 bg-fog rounded-md text-2 text-midnight/70">
                                      <Clock className="h-3.5 w-3.5 text-orange" />
                                      <span className="font-medium">{course.duration} days</span>
                                    </div>
                                    {course.difficulty && (
                                      <span className="px-2 py-1 bg-orange/10 text-orange rounded-md text-2 font-semibold">
                                        {course.difficulty}
                                      </span>
                                    )}
                                    {course.rating > 0 && (
                                      <div className="flex items-center gap-1.5 px-2 py-1 bg-yellow-50 rounded-md">
                                        <Star className="h-3.5 w-3.5 fill-yellow-400 text-yellow-400" />
                                        <span className="text-2 font-semibold text-midnight">{course.rating.toFixed(1)}</span>
                                      </div>
                                    )}
                                  </div>
                                </div>

                                {/* Bottom Section - Price and Action */}
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-4 border-t border-midnight/5 mt-auto shrink-0">
                                  {/* Price */}
                                  <div className="flex-1 min-w-0">
                                    {course.priceRange && course.priceRange.min !== undefined ? (
                                      <div>
                                        <div className="text-3 sm:text-4 font-bold text-forest mb-1">
                                          {formatCurrency(course.priceRange.min, course.priceRange.currency)}
                                        </div>
                                        <span className="text-1 sm:text-2 text-midnight/60">for {course.duration} days</span>
                                      </div>
                                    ) : course.price ? (
                                      <div>
                                        <div className="text-3 sm:text-4 font-bold text-forest mb-1">
                                          {formatCurrency(course.price, course.currency)}
                                        </div>
                                        <span className="text-1 sm:text-2 text-midnight/60">for {course.duration} days</span>
                                      </div>
                                    ) : (
                                      <div className="bg-fog rounded-lg p-3 border border-midnight/5">
                                        <span className="text-1 sm:text-2 text-midnight/60 font-medium">Contact for pricing</span>
                                      </div>
                                    )}
                                  </div>

                                  {/* Action Button */}
                                  <div className="sm:ml-4 w-full sm:w-auto">
                                    <button
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        openCourseInNewTab(schoolSlug, course.slug);
                                      }}
                                      className="w-full sm:w-auto px-6 py-2.5 bg-orange text-white rounded-lg font-semibold hover:bg-orange-dark transition-all duration-300 shadow-sm hover:shadow-md flex items-center justify-center gap-2 text-2"
                                    >
                                      <BookOpen className="h-4 w-4" />
                                      View Details
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </motion.div>
                        );
                      })}
                    </div>

                    {/* Pagination for Courses */}
                    <Pagination
                      currentPage={page}
                      totalPages={totalPages}
                      totalCount={totalItems}
                      itemsPerPage={12}
                      currentItemsCount={courses.length}
                      onPageChange={setPage}
                      loading={loading}
                      showCount={true}
                    />
                  </>
              )}

              {!loading && !error && searchType === 'schools' && schools.length === 0 && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-center py-16 bg-white rounded-xl border border-midnight/5 shadow-sm"
                >
                  <div className="w-16 h-16 bg-fog rounded-full flex items-center justify-center mx-auto mb-4">
                    <Search className="w-8 h-8 text-midnight/40" />
                  </div>
                  <h3 className="text-5 font-serif font-semibold text-midnight mb-2">No schools found</h3>
                  <p className="text-2 text-midnight/60 mb-6 max-w-md mx-auto">Try adjusting your search criteria or filters to find more results</p>
                  <button
                    onClick={() => {
                      setSelectedLocation('');
                      setSelectedStyle('');
                      setSearchTerm('');
                      setDebouncedSearchTerm('');
                      setSelectedYogaStyles([]);
                      setYogaAllianceCertified('both');
                      setBudgetMin(0);
                      setBudgetMax(BUDGET_RANGE_MAX);
                      setPage(1);
                    }}
                    className="bg-orange text-white px-6 py-3 rounded-lg text-2 font-semibold hover:bg-orange-dark transition-all duration-300 shadow-sm hover:shadow-md"
                  >
                    Clear All Filters
                  </button>
          </motion.div>
              )}

              {!loading && !error && searchType === 'schools' && schools.length > 0 && (
                <>
                  <div ref={resultsHeaderRef} className="mb-6">
                    <h2 className="heading-section font-serif font-semibold text-midnight mb-2">Available Schools</h2>
                    <p className="text-midnight/60">Showing {schools.length} of {totalItems} schools</p>
                  </div>
                  <div className="space-y-4">
                    {schools.map((school, index) => (
              <motion.div
                        key={school._id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: index * 0.05 }}
                        className="bg-white rounded-xl shadow-sm border border-midnight/5 overflow-hidden hover:shadow-md transition-all duration-300 group min-h-[280px] md:h-[280px]"
                      >
                        <div className="flex h-full min-h-0 flex-col md:flex-row">
                          {/* Left Side - Image fills fixed-height card column */}
                          <div className="relative h-48 w-full shrink-0 bg-fog md:h-full md:w-64 lg:w-80 md:min-h-0 overflow-hidden">
                            {(() => {
                              const mediaUrls = schoolMediaUrls.get(school._id);
                              const displayImage = mediaUrls?.images.coverImage || 
                                                 (mediaUrls?.images.gallery && mediaUrls.images.gallery.length > 0 ? mediaUrls.images.gallery[0] : null) ||
                                                 mediaUrls?.images.logo ||
                                                 school.images?.coverImage || 
                                                 (school.images?.gallery && school.images.gallery.length > 0 ? school.images.gallery[0] : null) || 
                                                 school.images?.logo;
                              return displayImage ? (
                                <img 
                                  src={displayImage} 
                                  alt={school.name}
                                  className="absolute inset-0 h-full w-full object-cover"
                                />
                              ) : null;
                            })() || (
                              <div className="absolute inset-0 flex items-center justify-center">
                                <BookOpen className="w-16 h-16 text-midnight/20" />
                              </div>
                            )}
                          </div>

                          {/* Right Side - Content */}
                          <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden p-6">
                            {/* Header Section */}
                            <div className="mb-2 shrink-0">
                              <div className="flex-1 min-w-0">
                                <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-3 mb-2">
                                  <h3 className="text-3 sm:text-4 font-serif font-semibold text-midnight line-clamp-2 leading-snug group-hover:text-orange transition-colors flex-1">
                                  {school.name}
                                </h3>
                              </div>
                                <div className="flex items-center gap-2 text-midnight/60 mb-3">
                                  <MapPin className="w-4 h-4 text-orange flex-shrink-0" />
                                  <p className="text-2 font-medium truncate">{school.location}</p>
                    </div>
                                <div className="flex items-center gap-3 sm:gap-4 flex-wrap">
                                  <div className="flex items-center gap-1.5">
                                    <Star className="w-4 h-4 text-yellow-400 fill-yellow-400 flex-shrink-0" />
                                    <span className="text-2 font-semibold text-midnight">{school.rating?.toFixed(1) || 'N/A'}</span>
                  </div>
                                  <span className="text-midnight/30 hidden sm:inline">•</span>
                                  <span className="text-2 text-midnight/60">{school.totalReviews || 0} reviews</span>
                            </div>
                    </div>
                  </div>

                            {/* Description and Features */}
                            <div className="mb-2">
                              <p className="text-1 text-midnight/70 leading-snug mb-2 line-clamp-1">
                                {school.shortDescription || school.description?.substring(0, 150) + '...'}
                          </p>

                  {/* Features */}
                          {(school.facilities && school.facilities.length > 0) && (
                                <div className="flex flex-wrap gap-1.5">
                                  {school.facilities.slice(0, 3).map((facility, idx) => (
                                    <div key={idx} className="flex items-center gap-1.5 px-2 py-1 bg-fog rounded-md">
                                      <CheckCircle className="w-3.5 h-3.5 text-orange flex-shrink-0" />
                                      <span className="text-1 text-midnight/70">{facility}</span>
                      </div>
                    ))}
                  </div>
                          )}
                            </div>

                            {/* Bottom Section - Actions (pricing hidden on listing cards) */}
                            <div className="mt-auto flex shrink-0 flex-col gap-2 border-t border-midnight/5 pt-3">
                              <div className="grid grid-cols-1 min-[480px]:grid-cols-2 gap-3">
                                <Link
                                  to={`/schools/${school.slug || school._id}`}
                                  className="min-w-0 flex"
                                >
                                  <button
                                    type="button"
                                    className="w-full min-h-[44px] box-border px-4 py-2.5 rounded-lg text-2 font-semibold border-2 border-orange bg-orange text-white hover:bg-orange-dark transition-all duration-300 shadow-sm hover:shadow-md flex items-center justify-center gap-2"
                                  >
                                    <BookOpen className="w-4 h-4 flex-shrink-0" />
                                    View Details
                                  </button>
                                </Link>
                                <Link to="/consultation" className="min-w-0 flex">
                                  <button
                                    type="button"
                                    className="w-full min-h-[44px] box-border px-4 py-2.5 rounded-lg text-2 font-semibold border-2 border-orange bg-white text-orange hover:bg-orange hover:text-white transition-all duration-300 flex items-center justify-center gap-2"
                                  >
                                    Get Advice
                                  </button>
                                </Link>
                              </div>
                            </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

                  {/* Pagination for Schools */}
                  <Pagination
                    currentPage={page}
                    totalPages={totalPages}
                    totalCount={totalItems}
                    itemsPerPage={SCHOOLS_PAGE_SIZE}
                    currentItemsCount={schools.length}
                    onPageChange={setPage}
                    loading={loading}
                    showCount={true}
                  />
                </>
          )}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-orange text-white">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="heading-section font-semibold mb-6">
              Need Help Choosing?
            </h2>
            <p className="text-4 opacity-95 mb-8 max-w-2xl mx-auto font-medium leading-relaxed">
              Our yoga experts are here to help you find the perfect school based on your goals, 
              experience level, and preferences. Book a free consultation today!
            </p>
            <Link to="/consultation">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-white text-orange px-8 py-4 rounded-lg font-semibold text-4 hover:bg-opacity-90 transition-all duration-300"
              >
                Book Free Consultation
              </motion.button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Mobile Filter Modal */}
      <AnimatePresence>
        {isMobileFilterOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999]"
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                width: '100vw',
                height: '100vh',
                zIndex: 9999
              }}
              onClick={() => setIsMobileFilterOpen(false)}
            />

            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.3 }}
              className="fixed inset-x-0 bottom-0 z-[10000] flex max-h-[90vh] min-h-0 flex-col rounded-t-3xl bg-white shadow-2xl"
              style={{ zIndex: 10000 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex-shrink-0 flex items-center justify-between p-4 border-b border-midnight/10">
                <div className="flex items-center gap-2">
                  <Filter className="w-5 h-5 text-orange" />
                  <h3 className="heading-section font-serif font-semibold text-midnight">Filters</h3>
                </div>
                <button
                  onClick={() => setIsMobileFilterOpen(false)}
                  className="p-2 hover:bg-fog rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-midnight" />
                </button>
              </div>

              {/* Scrollable Content — min-h-0 so this panel scrolls inside the flex column */}
              <div
                className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain p-4"
                style={{
                  WebkitOverflowScrolling: 'touch' as const,
                  touchAction: 'pan-y',
                }}
              >
                {/* Search Type Toggle */}
                <div className="mb-4">
                  <label className="block text-2 font-semibold text-midnight mb-2">Search Type</label>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setSearchType('courses');
                        setSearchTerm('');
                        setDebouncedSearchTerm('');
                        setSelectedLocation('');
                        setSelectedStyle('');
                        setSelectedYogaStyles([]);
                        setYogaAllianceCertified('both');
                        setBudgetMin(0);
                        setBudgetMax(BUDGET_RANGE_MAX);
                        setPage(1);
                      }}
                      className={`flex-1 px-4 py-2.5 text-2 rounded-xl font-semibold transition-all ${
                        searchType === 'courses'
                          ? 'bg-orange text-white shadow-md hover:bg-orange-dark'
                          : 'bg-fog text-midnight/70 hover:bg-fog-dark'
                      }`}
                    >
                      Courses
                    </button>
                    {/* Schools search type hidden — courses-only listing for now
                    <button
                      onClick={() => {
                        setSearchType('schools');
                        setSearchTerm('');
                        setDebouncedSearchTerm('');
                        setSelectedLocation('');
                        setSelectedStyle('');
                        setSelectedYogaStyles([]);
                        setYogaAllianceCertified('both');
                        setBudgetMin(0);
                        setBudgetMax(BUDGET_RANGE_MAX);
                        setPage(1);
                      }}
                      className={`flex-1 px-4 py-2.5 text-2 rounded-xl font-semibold transition-all ${
                        searchType === 'schools'
                          ? 'bg-orange text-white shadow-md hover:bg-orange-dark'
                          : 'bg-fog text-midnight/70 hover:bg-fog-dark'
                      }`}
                    >
                      Schools
                    </button>
                    */}
                  </div>
                </div>
                
                {/* Search */}
                <div className="mb-4">
                  <label className="block text-2 font-semibold text-midnight mb-2">Search</label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-midnight/40" />
                    <input
                      type="text"
                      placeholder="Search courses..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 text-2 border-2 border-midnight/10 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange focus:border-orange transition-all text-midnight bg-white placeholder-midnight/40"
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label className="mb-2 flex items-center gap-2 text-2 font-semibold text-midnight">
                    <MapPin className="h-4 w-4 text-orange" />
                    Location
                  </label>
                  <ListingLocationSelect
                    value={selectedLocation}
                    onChange={(v) => {
                      setSelectedLocation(v);
                      setPage(1);
                    }}
                    countries={locationCountries}
                    cities={locationCities}
                  />
                </div>

                <div className="mb-4">
                  <label className="mb-2 flex items-center gap-2 text-2 font-semibold text-midnight">
                    <Banknote className="h-4 w-4 text-orange" />
                    Budget (course fee)
                  </label>
                  <ListingBudgetSliders
                    min={budgetMin}
                    max={budgetMax}
                    onMinChange={(n) => {
                      setBudgetMin(n);
                      setPage(1);
                    }}
                    onMaxChange={(n) => {
                      setBudgetMax(n);
                      setPage(1);
                    }}
                  />
                </div>

                {/* Yoga Styles Filter */}
                <div className="mb-4">
                  <label className="block text-2 font-semibold text-midnight mb-2 flex items-center gap-2">
                    <BookOpen className="w-4 h-4 text-orange" />
                    Yoga Styles
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {yogaStyles.map((style) => (
                      <label
                        key={style}
                        className="flex items-center gap-2 cursor-pointer hover:bg-fog px-2 py-1 rounded transition-colors"
                      >
                        <input
                          type="checkbox"
                          checked={selectedYogaStyles.includes(style)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedYogaStyles([...selectedYogaStyles, style]);
                            } else {
                              setSelectedYogaStyles(selectedYogaStyles.filter(s => s !== style));
                            }
                            setPage(1);
                          }}
                          className="w-4 h-4 rounded border-midnight/20 focus:ring-2 focus:ring-orange cursor-pointer"
                        />
                        <span className="text-2 text-midnight/70">{style}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Yoga Alliance Certified Filter */}
                <div className="mb-4">
                  <label className="block text-2 font-semibold text-midnight mb-2 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-orange" />
                    Yoga Alliance Certified
                  </label>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setYogaAllianceCertified('both')}
                      className={`flex-1 px-4 py-2.5 text-2 rounded-xl font-semibold transition-all ${
                        yogaAllianceCertified === 'both'
                          ? 'bg-orange text-white shadow-md hover:bg-orange-dark'
                          : 'bg-fog text-midnight/70 hover:bg-fog-dark'
                      }`}
                    >
                      Both
                    </button>
                    <button
                      onClick={() => setYogaAllianceCertified('yes')}
                      className={`flex-1 px-4 py-2.5 text-2 rounded-xl font-semibold transition-all ${
                        yogaAllianceCertified === 'yes'
                          ? 'bg-orange text-white shadow-md hover:bg-orange-dark'
                          : 'bg-fog text-midnight/70 hover:bg-fog-dark'
                      }`}
                    >
                      Yes
                    </button>
                    <button
                      onClick={() => setYogaAllianceCertified('no')}
                      className={`flex-1 px-4 py-2.5 text-2 rounded-xl font-semibold transition-all ${
                        yogaAllianceCertified === 'no'
                          ? 'bg-orange text-white shadow-md hover:bg-orange-dark'
                          : 'bg-fog text-midnight/70 hover:bg-fog-dark'
                      }`}
                    >
                      No
                    </button>
                  </div>
                </div>

              </div>

              {/* Footer with Apply and Clear Buttons */}
              <div className="flex-shrink-0 p-4 border-t border-midnight/10 bg-white">
                <div className="flex gap-3">
                  {(selectedLocation ||
                    selectedStyle ||
                    selectedYogaStyles.length > 0 ||
                    yogaAllianceCertified !== 'both' ||
                    searchTerm ||
                    isListingBudgetActive(budgetMin, budgetMax)) && (
                    <button
                      onClick={() => {
                        setSelectedLocation('');
                        setSelectedStyle('');
                        setSelectedYogaStyles([]);
                        setYogaAllianceCertified('both');
                        setSearchTerm('');
                        setDebouncedSearchTerm('');
                        setBudgetMin(0);
                        setBudgetMax(BUDGET_RANGE_MAX);
                        setPage(1);
                        setIsMobileFilterOpen(false);
                        setTimeout(() => {
                          if (resultsHeaderRef.current) {
                            resultsHeaderRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                          }
                        }, 100);
                      }}
                      className="flex-1 px-4 py-3 border-2 border-gray-300 text-gray-700 rounded-xl font-semibold text-2 hover:bg-gray-50 transition-colors"
                    >
                      Clear
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setDebouncedSearchTerm(searchTerm);
                      setPage(1);
                      setIsMobileFilterOpen(false);
                      setTimeout(() => {
                        if (resultsHeaderRef.current) {
                          resultsHeaderRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }
                      }, 100);
                    }}
                    className={`px-4 py-3 bg-orange text-white rounded-xl font-semibold text-2 hover:bg-orange-dark transition-colors shadow-md ${
                      selectedLocation ||
                      selectedStyle ||
                      selectedYogaStyles.length > 0 ||
                      yogaAllianceCertified !== 'both' ||
                      searchTerm ||
                      isListingBudgetActive(budgetMin, budgetMax)
                        ? 'flex-1' 
                        : 'w-full'
                    }`}
                  >
                    Apply Filters
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
      </div>
    </div>
  );
};

export default Schools; 